import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  APP_ID,
  DEFAULT_CURRENCY_CODE,
  enableProdMode,
  inject,
  importProvidersFrom,
  isDevMode,
  provideAppInitializer,
  signal,
} from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter } from '@angular/router';

import { CoreEffects } from '@next/core-lib';
import { provideAgrAdvisorRelationshipsSelector } from '@next/core-lib/advisor-relationships-selector';
import { provideAgrCore } from '@next/core-lib/app';
import { I18nBrowserEffects, provideAgrBrowser } from '@next/core-lib/browser';
import { provideAgrCart } from '@next/core-lib/cart';
import { provideAgrLocationSummary } from '@next/core-lib/components';
import { provideAgrElasticApi } from '@next/core-lib/elastic';
import { ENVIRONMENT_MAPPER } from '@next/core-lib/environment-browser';
import { AgrExportModule } from '@next/core-lib/export';
import { FeatureFlagService } from '@next/core-lib/featureflag';
import { AgrNumberDirective } from '@next/core-lib/form';
import { AgrDatePipe, AgrNumberPipe, I18nEffects, provideAgrI18nShared } from '@next/core-lib/i18n';
import { NavigationEffects, provideAgrNavigation } from '@next/core-lib/navigation';
import { RemoveSpecialCharactersPipe } from '@next/core-lib/pipes';
import { AgrPowerBiEmbeddedModule } from '@next/core-lib/powerbi-embedded';
import { provideAgrRouterStore } from '@next/core-lib/router-store';
import { provideAgrSettings } from '@next/core-lib/settings';
import { SiteEffects, provideAgrSite } from '@next/core-lib/site';
import { provideAgrTable } from '@next/core-lib/table';
import { provideAgrWrapperSiteSidenav } from '@next/core-lib/wrapper-site-sidenav';
import { provideEffects } from '@ngrx/effects';
import { Action, ActionReducer, MetaReducer, provideState, provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';

import { localStorageSync } from 'ngrx-store-localstorage';

import { AppComponent } from './app/app.component';
import { appRoutes } from './app/app.routes';
import { ContractEffects } from './app/contract/store/contract.effects';
import { contractReducer } from './app/contract/store/contract.reducer';
import { DeliveryDataEffects } from './app/delivery-data/store/delivery-data.effects';
import { deliveryDataReducer } from './app/delivery-data/store/delivery-data.reducer';
import { greenhouseReducer } from './app/greenhouse-gas/store/greenhouse.reducer';
import { IdentificationEffects } from './app/identification/store/effects';
import { identificationReducer } from './app/identification/store/reducer';
import { InvoiceDataEffects } from './app/invoice-data/store/invoice-data.effects';
import { invoiceDataReducer } from './app/invoice-data/store/invoice-data.reducer';
import { mineralReducer } from './app/mineral/store/mineral.reducer';
import { PreferencesEffects } from './app/preferences/store/preferences.effects';
import { preferencesReducer } from './app/preferences/store/preferences.reducer';
import { provideIconInitializer } from './app/shared/register-shared-icons';
import { AppEffects } from './app/store/effects';
import { appReducer } from './app/store/reducer';
import { environment } from './environments/environment';
import { parseMySiteEnvironment } from './environments/parse-environment';

declare global {
  var CONFIG_BUILD_ID: string | undefined;
}

type RootStateShape = Record<string, never>;

const localStorageSyncReducer = (
  reducer: ActionReducer<RootStateShape, Action<string>>,
): ActionReducer<RootStateShape, Action<string>> =>
  localStorageSync({
    keys: ['filterModule', 'mineralModule', 'greenhouseModule'],
    rehydrate: true,
    restoreDates: false,
    checkStorageAvailability: true,
  })(reducer);

const rootStoreConfig = {
  metaReducers: [localStorageSyncReducer] as Array<MetaReducer<RootStateShape, Action<string>>>,
  runtimeChecks: {
    strictStateImmutability: false,
    strictActionImmutability: false,
    strictStateSerializability: false,
    strictActionSerializability: false,
  },
};

const loadedConfig = signal(environment);

const toRuntimeConfigValue = (value: unknown): string | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (Array.isArray(value)) {
    return value.join(',');
  }

  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }

  if (typeof value === 'number' || typeof value === 'string') {
    return `${value}`;
  }

  return undefined;
};

const getDefaultRuntimeConfig = (): Record<string, string> =>
  Object.entries(environment).reduce(
    (runtimeConfig, [key, value]) => {
      const runtimeValue = toRuntimeConfigValue(value);

      if (runtimeValue !== undefined) {
        runtimeConfig[key] = runtimeValue;
      }

      return runtimeConfig;
    },
    {} as Record<string, string>,
  );

const getSafeAppId = (value: string | undefined): string => {
  const sanitized = (value ?? '').replace(/[^A-Za-z0-9_-]/g, '');
  return sanitized || 'mysite';
};

const setupRuntimeConfig = async (): Promise<void> => {
  try {
    const buildId = globalThis.CONFIG_BUILD_ID;
    const configUrl = buildId ? `/assets/config.json?v=${buildId}` : '/assets/config.json';
    const response = await globalThis.fetch(configUrl);
    if (response.ok) {
      const config = (await response.json()) as Record<string, string>;

      if (Object.keys(config).length === 0) {
        return;
      }

      loadedConfig.set(parseMySiteEnvironment({ ...getDefaultRuntimeConfig(), ...config }));
    } else {
      console.warn('No runtime configuration found, using default environment settings.');
    }
  } catch (e) {
    console.warn(
      'Ignoring invalid runtime configuration and using default environment settings.',
      e,
    );
  }
};

if (environment.PRODUCTION) {
  enableProdMode();
}

const startApplication = async (): Promise<void> => {
  await setupRuntimeConfig();
  await bootstrapApplication(AppComponent, {
    providers: [
      provideRouter(appRoutes),
      // Core provider functions (replacing AgrBrowserModule and AgrAppModule)
      provideAgrBrowser({ enableServiceWorker: loadedConfig().PRODUCTION }),
      provideAgrCore(),
      // Feature providers
      provideAgrCart(),
      provideAgrTable(),
      provideAgrWrapperSiteSidenav(),
      provideAgrElasticApi(),
      provideAgrI18nShared(),
      importProvidersFrom(AgrExportModule),
      // Icon initialization for LocationSummary component
      provideAgrLocationSummary(),
      // Settings service and store
      provideAgrSettings(),
      // AdvisorRelationshipsSelector service
      provideAgrAdvisorRelationshipsSelector(),
      // Site and Navigation state
      provideAgrSite(),
      provideAgrNavigation(),
      provideAppInitializer(() => {
        const featureFlagService = inject(FeatureFlagService);
        featureFlagService.initialize('dashboard');
      }),
      provideIconInitializer(),
      provideHttpClient(withInterceptorsFromDi()),
      provideStore({}, rootStoreConfig),
      provideState('app', appReducer),
      provideState('contractModule', contractReducer),
      provideState('deliveryDataModule', deliveryDataReducer),
      provideState('greenhouseModule', greenhouseReducer),
      provideState('identification', identificationReducer),
      provideState('invoiceDataModule', invoiceDataReducer),
      provideState('mineralModule', mineralReducer),
      provideState('preferencesModule', preferencesReducer),

      provideEffects(
        CoreEffects,
        ContractEffects,
        DeliveryDataEffects,
        IdentificationEffects,
        I18nEffects,
        I18nBrowserEffects,
        InvoiceDataEffects,
        NavigationEffects,
        PreferencesEffects,
        SiteEffects,
        AppEffects,
      ),
      provideAgrRouterStore(),
      importProvidersFrom(AgrPowerBiEmbeddedModule),
      provideStoreDevtools({
        maxAge: 25,
        logOnly: !isDevMode(),
        autoPause: true,
        trace: false,
        traceLimit: 75,
        connectInZone: false,
      }),
      {
        provide: ENVIRONMENT_MAPPER,
        useFactory: () => loadedConfig(),
      },
      {
        provide: APP_ID,
        useFactory: () => getSafeAppId(loadedConfig().APPLICATION_NAME),
      },
      {
        provide: DEFAULT_CURRENCY_CODE,
        useValue: 'EUR',
      },
      AgrNumberDirective,
      AgrNumberPipe,
      AgrDatePipe,
      RemoveSpecialCharactersPipe,
    ],
  });
};

void startApplication().catch((err) => console.error(err));
